/**
 * Patient Portal Service
 *
 * API calls for patient-specific endpoints
 * Uses separate patient API client (not shared with staff)
 */

import { patientGet, patientApi } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'

// =============================================================================
// Types
// =============================================================================

export interface PatientStudy {
  id: string
  studyInstanceUID: string
  accessionNumber: string | null
  patientId: string
  modality: string
  studyDate: string
  studyTime: string | null
  studyDescription: string | null
  referringPhysician: string | null
  performingPhysician: string | null
  bodyPartExamined: string | null
  numberOfSeries: number
  numberOfInstances: number
  institutionName: string | null
  status: string
  reportId: string | null
  createdAt: string
  updatedAt: string
}

export interface PatientStudiesResponse {
  studies: PatientStudy[]
  total: number
}

export interface PatientProfile {
  id: string
  firstName: string
  lastName: string
  documentType: string
  documentNumber: string
  email: string | null
  phone: string | null
  birthDate: string | null
  gender: string | null
}

export interface PatientReport {
  id: string
  studyId: string
  findings: string
  impression: string
  conclusion: string
  recommendations: string | null
  status: string
  radiologistId: string
  signedAt: string | null
  createdAt: string
  updatedAt: string
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current patient's profile
 */
export async function getMyProfile(): Promise<PatientProfile> {
  const response = await patientGet<{ patient: PatientProfile }>(API_ROUTES.PATIENT_PORTAL.ME)
  return response.patient
}

/**
 * Get patient's own studies
 */
export async function getMyStudies(params?: {
  limit?: number
  offset?: number
}): Promise<PatientStudiesResponse> {
  const queryParams = new URLSearchParams()
  if (params?.limit) queryParams.set('limit', params.limit.toString())
  if (params?.offset) queryParams.set('offset', params.offset.toString())

  const url = queryParams.toString()
    ? `${API_ROUTES.PATIENT_PORTAL.STUDIES}?${queryParams.toString()}`
    : API_ROUTES.PATIENT_PORTAL.STUDIES

  return patientGet<PatientStudiesResponse>(url)
}

/**
 * Get a specific study by ID
 */
export async function getMyStudyById(id: string): Promise<PatientStudy> {
  const response = await patientGet<{ study: PatientStudy }>(API_ROUTES.PATIENT_PORTAL.STUDY_BY_ID(id))
  return response.study
}

/**
 * Get report for a study
 */
/**
 * Una nota que el médico agregó al informe después de firmarlo.
 *
 * Viaja con el informe y no aparte: el informe y lo que se le añadió son un
 * solo documento. Pedirlos por separado permitiría que la pantalla pintara
 * uno sin el otro.
 */
export interface AddendumDelPaciente {
  id: string
  content: string
  createdAt: string
  firmante?: { nombre: string; email: string } | null
}

export interface InformeConAddenda {
  report: PatientReport
  addenda: AddendumDelPaciente[]
}

export async function getMyStudyReportConAddenda(
  studyId: string
): Promise<InformeConAddenda> {
  const response = await patientGet<InformeConAddenda>(
    API_ROUTES.PATIENT_PORTAL.STUDY_REPORT(studyId)
  )
  return { report: response.report, addenda: response.addenda ?? [] }
}

export async function getMyStudyReport(studyId: string): Promise<PatientReport> {
  const response = await patientGet<{ report: PatientReport }>(API_ROUTES.PATIENT_PORTAL.STUDY_REPORT(studyId))
  return response.report
}

// =============================================================================
// Attachments
// =============================================================================

export interface PatientAttachment {
  id: string
  attachmentType: string
  fileName: string
  fileSize: number | null
  mimeType: string | null
  description: string | null
  createdAt: string
}

/**
 * Get attachments for a study
 */
export async function getMyStudyAttachments(studyId: string): Promise<PatientAttachment[]> {
  const response = await patientGet<{ attachments: PatientAttachment[] }>(
    API_ROUTES.PATIENT_PORTAL.STUDY_ATTACHMENTS(studyId)
  )
  return response.attachments
}

/**
 * Get download URL for an attachment
 */
export async function getAttachmentDownloadUrl(
  studyId: string,
  attachmentId: string
): Promise<{ downloadUrl: string; fileName: string; mimeType: string | null }> {
  return patientGet(API_ROUTES.PATIENT_PORTAL.ATTACHMENT_DOWNLOAD(studyId, attachmentId))
}

// =============================================================================
// Study Images (Instances)
// =============================================================================

export interface PatientStudyInstance {
  id: string
  /** El visor agrupa por serie; sin esto todas las imágenes caen en un montón. */
  seriesInstanceUID: string
  instanceNumber: number | null
  seriesNumber: number | null
  seriesDescription: string | null
  /**
   * Archivo DICOM original. Es lo que lee el visor: la imagen convertida a
   * JPG sirve para la galería, pero no conserva los valores con los que se
   * ajusta ventana y nivel.
   */
  fileUrl: string
  imageUrl: string
  thumbnailUrl: string | null
}

export interface PatientStudySeries {
  seriesInstanceUID: string
  seriesNumber: number | null
  seriesDescription: string | null
  instanceCount: number
}

/**
 * Get all images/instances for a study
 */
export async function getMyStudyInstances(studyId: string): Promise<{
  instances: PatientStudyInstance[]
  series: PatientStudySeries[]
  total: number
}> {
  return patientGet(API_ROUTES.PATIENT_PORTAL.STUDY_INSTANCES(studyId))
}

/**
 * Get image URL for a specific instance (PNG/JPG)
 */
export function getInstanceImageUrl(
  studyId: string,
  instanceId: string,
  options?: { format?: 'png' | 'jpeg'; width?: number; height?: number }
): string {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
  let url = `${baseUrl}${API_ROUTES.PATIENT_PORTAL.INSTANCE_IMAGE(studyId, instanceId)}`

  const params = new URLSearchParams()
  if (options?.format) params.set('format', options.format)
  if (options?.width) params.set('width', options.width.toString())
  if (options?.height) params.set('height', options.height.toString())

  const queryString = params.toString()
  if (queryString) url += `?${queryString}`

  return url
}

/**
 * Get thumbnail URL for a specific instance
 */
export function getInstanceThumbnailUrl(studyId: string, instanceId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
  return `${baseUrl}${API_ROUTES.PATIENT_PORTAL.INSTANCE_THUMBNAIL(studyId, instanceId)}`
}

// =============================================================================
// Descarga del examen completo
// =============================================================================

/** Lo que el paciente puede pedir. */
export type FormatoDeDescarga = 'dicom' | 'png'

/**
 * Descarga el examen completo, comprimido.
 *
 * Las dos opciones no son un capricho. El DICOM es el original y lo abre
 * cualquier visor de cualquier clínica: es lo que el paciente entrega cuando
 * le piden un estudio comparativo en otra entidad. El PNG sirve para mirar y
 * para adjuntar a un correo, pero con él nadie puede informar.
 */
export async function descargarMiEstudio(
  studyId: string,
  formato: FormatoDeDescarga
): Promise<{ blob: Blob; nombre: string }> {
  const response = await patientApi.get(
    `${API_ROUTES.PATIENT_PORTAL.STUDY_ARCHIVE(studyId)}?formato=${formato}`,
    { responseType: 'blob' }
  )

  const cabecera = response.headers?.['content-disposition']
  const coincidencia =
    typeof cabecera === 'string' ? cabecera.match(/filename="?([^";]+)"?/) : null

  return {
    blob: response.data as Blob,
    nombre: coincidencia?.[1] ?? `mis_imagenes_${formato}.zip`,
  }
}

/** Le entrega el archivo al navegador. */
export function guardarArchivo(blob: Blob, nombre: string): void {
  const url = window.URL.createObjectURL(blob)
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = nombre
  document.body.appendChild(enlace)
  enlace.click()
  document.body.removeChild(enlace)
  window.URL.revokeObjectURL(url)
}
